import { defaultDocument, useEventListener } from '@vueuse/core';
import { getCurrentScope, onScopeDispose, ref, type Ref } from 'vue';
import type { ProductSearch } from '@/entities/product';
import { SERP_SEARCH_EXPIRATION_MS } from '../config/searchExpiration';

export interface SearchExpiration {
  isExpired: Readonly<Ref<boolean>>;
  dismiss(): void;
}

export function useSearchExpiration(productSearch: ProductSearch): SearchExpiration {
  const isExpired = ref(false);

  let expiresAt: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function stopTimer(): void {
    if (!timer) return;

    clearTimeout(timer);
    timer = null;
  }

  function expire(): void {
    stopTimer();
    expiresAt = null;
    isExpired.value = true;
  }

  function restart(): void {
    stopTimer();
    isExpired.value = false;
    expiresAt = Date.now() + SERP_SEARCH_EXPIRATION_MS;
    timer = setTimeout(expire, SERP_SEARCH_EXPIRATION_MS);
  }

  function checkExpiration(): void {
    if (expiresAt !== null && Date.now() >= expiresAt) expire();
  }

  useEventListener(defaultDocument, 'visibilitychange', checkExpiration);

  const unsubscribe = productSearch.on('start', restart);

  if (getCurrentScope()) {
    onScopeDispose(() => {
      stopTimer();
      unsubscribe();
    });
  }

  return { isExpired, dismiss: restart };
}
